import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { useProject, useUpdateProject } from "@/hooks/useProjects";
import { useProjectDateChangeRequests } from "@/hooks/useDateChangeRequests";
import { cn } from "@/lib/utils";
import { Project } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import UnifiedTeamSection from "@/components/projects/UnifiedTeamSection";
import MeetingTimeline from "@/components/MeetingTimeline";
import MeetingIntelligenceCard from "@/components/MeetingIntelligenceCard";
import ToolsSection from "@/components/ToolsSection";
import Phase0Page from "@/components/projects/phase0/Phase0Page";
import { PhaseGovernanceSection } from "@/components/projects/governance/PhaseGovernanceSection";
import RiskDashboard from "@/components/projects/RiskDashboard";
import BusinessContextCard from "@/components/projects/BusinessContextCard";
import WeeklyReportSection from "@/components/projects/WeeklyReportSection";
import LatestWeeklyReportCard from "@/components/projects/LatestWeeklyReportCard";
import DeviationSignalsBanner from "@/components/projects/DeviationSignalsBanner";
import { ProjectInfoCard } from "@/components/projects/ProjectInfoCard";
import { ProjectInfoSheet } from "@/components/projects/ProjectInfoSheet";
import { ProjectTeamCard } from "@/components/projects/ProjectTeamCard";
import { ProjectToolsCard } from "@/components/projects/ProjectToolsCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Modal, ModalBody, ModalFooter } from "@/components/ui/modal";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Tabs } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { DateChangePendingBanner } from "@/components/projects/DateChangePendingBanner";
import { ClosureRequestPanel } from "@/components/projects/ClosureRequestPanel";
import { PHASE_NAMES, phaseLabel } from "@/lib/phases";

const PHASE_ORDER = [
  "Phase 0",
  "Phase 1",
  "Phase 2",
  "Phase 3",
  "Phase 4",
] as const;

/**
 * The six project tabs. Module-scope because the bar is rendered from one of two positions
 * depending on the active tab (see `tabBar` in the page body) and the set must be identical in
 * both.
 */
const PROJECT_TABS = [
  { id: "overview", label: "Overview" },
  { id: "meetings", label: "Meetings" },
  { id: "weekly-reports", label: "Weekly Reports" },
  { id: "team", label: "Team & Resources" },
  // Label only (demo L638–647: "it's artifacts and it's context"); the id / ?tab=phase0 URL is stable.
  { id: "phase0", label: "Artifacts & Context" },
  { id: "settings", label: "Settings" },
];

// ─── Phase stepper ───────────────────────────────────────────────────────────

function PhaseStepper({ currentPhase }: { currentPhase: string }): JSX.Element {
  const currentIndex = PHASE_ORDER.indexOf(
    currentPhase as (typeof PHASE_ORDER)[number],
  );
  return (
    <ol className="flex items-center">
      {PHASE_ORDER.map((phase, i) => {
        const completed = i < currentIndex;
        const current = i === currentIndex;
        return (
          <li key={phase} className="flex items-start">
            <div className="flex flex-col items-center gap-1.5">
              <span
                className={cn(
                  "flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors",
                  completed && "border-[#497d00] bg-[#497d00] text-white",
                  current && "border-violet-950 bg-white text-neutral-800",
                  !completed &&
                    !current &&
                    "border-outlines bg-white text-text-secondary",
                )}
                aria-current={current ? "step" : undefined}
              >
                {completed ? <CheckCircle2 className="h-4 w-4" /> : i}
              </span>
              <span
                className={cn(
                  "hidden text-[14px] font-medium sm:block",
                  current ? "text-violet-950" : "text-text-secondary",
                )}
              >
                {phase}
              </span>
              {/* Canonical name under the number. Wraps to two short lines and only appears from
                  lg up, where the five steps have room; below that the number alone is shown. */}
              <span
                className={cn(
                  "-mt-1 hidden max-w-[5.5rem] text-center text-xs leading-tight lg:block",
                  current ? "text-violet-950" : "text-text-secondary",
                )}
              >
                {PHASE_NAMES[i]}
              </span>
            </div>
            {i < PHASE_ORDER.length - 1 && (
              // Pinned to the circle's vertical centre (h-9 → 18px) so it stays aligned however
              // many label lines sit under the circles.
              <span
                aria-hidden="true"
                className={cn(
                  "mx-2 mt-[17px] h-0.5 w-6 shrink-0 self-start rounded-full transition-colors xl:mx-4 xl:w-10",
                  i < currentIndex ? "bg-[#497d00]" : "bg-violet-200",
                )}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ─── Project closure ──────────────────────────────────────────────────────────

/**
 * T-42 (V054): leadership/admin close (and reopen) directly; PM/SA raise a closure request that
 * leadership approves. ClosureRequestPanel renders the request form / pending state / inline
 * approve-reject; the direct close/reopen modal below is approver-only.
 */
function ClosureSection({ project }: { project: Project }): JSX.Element {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const updateProject = useUpdateProject(project.jira_key);
  const { user } = useAuth();
  const role = user?.role ?? "";
  const isApprover = ["leadership", "admin"].includes(role);
  const isRequester = ["pm", "sa"].includes(role);

  if (!isApprover && !isRequester) return <></>;

  const isClosed = project.status === "Closed";

  if (!isApprover) {
    return (
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-text-primary">
            Project Closure
          </h3>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isClosed && (
            <p className="text-sm text-text-secondary">
              Closing this project marks it as complete and archives it. Closure
              needs leadership approval — request it here and leadership will be
              notified.
            </p>
          )}
          <ClosureRequestPanel project={project} mode="requester" />
        </CardContent>
      </Card>
    );
  }

  const handleToggle = async (): Promise<void> => {
    try {
      await updateProject.mutateAsync({
        status: isClosed ? "Active" : "Closed",
      });
      toast.success(
        isClosed ? "Project reopened" : "Project closed and archived",
      );
      setConfirmOpen(false);
    } catch {
      // global interceptor shows error
    }
  };

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-semibold text-text-primary">
          Project Closure
        </h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-text-secondary">
          {isClosed
            ? "This project is closed and archived. Reopen it to resume active delivery."
            : "Closing this project marks it as complete and archives it. All data is preserved."}
        </p>
        <ClosureRequestPanel project={project} mode="approver" />
        <Button
          variant={isClosed ? "outline" : "danger"}
          onClick={() => setConfirmOpen(true)}
          disabled={updateProject.isPending}
        >
          {updateProject.isPending && (
            <Loader2 className="h-4 w-4 animate-spin" />
          )}
          {isClosed ? "Reopen Project" : "Close Project"}
        </Button>
      </CardContent>

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <ModalBody>
          <h3 className="text-base font-semibold text-text-primary mb-2">
            {isClosed ? "Reopen this project?" : "Close this project?"}
          </h3>
          <p className="text-sm text-text-secondary">
            {isClosed
              ? "The project will be set back to Active. Team members will be able to make changes again."
              : "The project will be marked as Closed. It will remain visible and all data is preserved, but it will be filtered from the active view by default."}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant={isClosed ? "primary" : "danger"}
            onClick={() => void handleToggle()}
            disabled={updateProject.isPending}
          >
            {updateProject.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            {isClosed ? "Reopen" : "Close Project"}
          </Button>
        </ModalFooter>
      </Modal>
    </Card>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function ProjectDetailPage(): JSX.Element {
  const { projectId } = useParams<{ projectId: string }>();
  const id = projectId ?? "";

  // URL-driven tab state — the active tab persists in the ?tab= query param so it survives
  // refresh, back/forward, and deep links. Falls back to 'overview' when absent.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "overview";

  const handleTabChange = (tab: string): void => {
    setSearchParams({ tab }, { replace: true });
  };

  // The P0 Artifacts tab is the one full-bleed surface on this page: its workspace measures its own
  // top and claims the rest of the viewport, so it is the only tab whose layout is sensitive to the
  // height of the chrome above it. Everything keyed off this flag exists for that reason.
  const isPhase0 = activeTab === "phase0";

  // One element, two possible homes — inside the sticky chrome when compact, below it otherwise.
  // Declared once so the tab set cannot drift between the two branches.
  const tabBar = (
    <Tabs
      active={activeTab}
      onChange={handleTabChange}
      size={isPhase0 ? "sm" : "default"}
      // Inside the chrome the chrome's own bottom border is the rule, so the tab bar drops its own.
      className={cn(
        "w-full justify-start overflow-x-auto",
        isPhase0 && "border-b-0",
      )}
      tabs={PROJECT_TABS}
    />
  );

  const projectQuery = useProject(id);
  // F-S2: pending date change requests for this project (banner + per-field "Pending" badge).
  const dateChangeRequestsQuery = useProjectDateChangeRequests(id);
  const pendingDateRequests = dateChangeRequestsQuery.data ?? [];

  const project = projectQuery.data as Project | undefined;

  if (projectQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-40 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (projectQuery.isError || !project) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Failed to load project</AlertTitle>
        <AlertDescription className="flex flex-col items-start gap-3">
          <span>
            The project could not be loaded. It may not exist or you may not
            have access.
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void projectQuery.refetch()}
          >
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={cn(isPhase0 ? "flex flex-col gap-3" : "space-y-6")}>
      {/* ── Page chrome (sticky) ──────────────────────────────────────────────────────────────────
          COMPACT on the P0 Artifacts tab, full everywhere else.

          WHY: that tab hands its entire surface to the chat/file workspace, which sizes itself to
          `window.innerHeight − (its own top) − page gutter` (Phase0Page, Item 6). The chrome above
          it is `sticky`, so it still occupies flow space and every pixel of it is a pixel taken off
          the transcript. Full chrome — breadcrumb row + 2xl title row + PhaseStepper + three 16px
          gaps + 16px bottom pad — measures ~230px. Compact chrome is ~68px: one identity line plus
          the tab bar.

          The tab bar MOVES IN HERE when compact rather than staying below. Two levels of navigation
          were stacking (these six project tabs, then Phase0Page's own FileTabStrip), and the outer
          one scrolled away the moment the workspace needed room. Inside the sticky block it stays
          put, which is what makes the other five tabs reachable from a full-viewport chat. */}
      <div
        data-testid="project-chrome"
        data-compact={isPhase0 ? "true" : "false"}
        className={cn(
          "z-20 -mx-4 border-b border-outlines bg-white px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8",
          isPhase0
            ? // `top-0`, NOT `top-[-32px]`: nothing in the compact chrome may scroll away — it is the
              // only navigation left on screen.
              "sticky top-0 space-y-1.5 pb-0 pt-2"
            : // `top-[-32px]` cancels <main>'s `--spacing-page` padding so the block pins flush to
              // the viewport, letting the breadcrumb row — and only it — scroll off.
              "sticky top-[-32px] space-y-4 pb-4 pt-1",
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          <nav
            aria-label="Breadcrumb"
            className="flex min-w-0 items-center gap-2 text-sm"
          >
            <Link
              to="/projects"
              className="shrink-0 text-text-secondary transition-colors hover:text-text-primary"
            >
              Projects
            </Link>
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-text-secondary/50" />
            <span
              className={cn(
                "truncate text-text-primary",
                isPhase0 ? "font-semibold" : "font-medium",
              )}
            >
              {project.title}
            </span>
          </nav>

          {/* Compact chrome has no title row, so the identity badges ride the breadcrumb line.
              They are outside the <nav> — they are not navigation. */}
          {isPhase0 && (
            <>
              <Badge
                variant="neutral"
                className="shrink-0 font-mono text-[11px]"
              >
                {project.jira_key}
              </Badge>
              <Badge variant="info" className="shrink-0 text-[11px]">
                {phaseLabel(project.current_phase)}
              </Badge>
              {/* The visible <h1> is gone when compact; the document still needs one. */}
              <h1 className="sr-only">{project.title}</h1>
            </>
          )}
        </div>

        {!isPhase0 && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-text-primary">
                  {project.title}
                </h1>
                <Badge variant="neutral" className="font-mono">
                  {project.jira_key}
                </Badge>
              </div>
              <div className="flex items-center gap-3">
                {/* T-11: presales context opens on demand in a side panel, not on the Overview. */}
                <ProjectInfoSheet project={project} />
                <Badge variant="info">
                  {phaseLabel(project.current_phase)}
                </Badge>
              </div>
            </div>

            <PhaseStepper currentPhase={project.current_phase} />
          </>
        )}

        {isPhase0 && tabBar}
      </div>

      {/* Tabbed layout */}
      <div className={cn(isPhase0 ? "flex min-h-0 flex-col" : "space-y-6")}>
        {!isPhase0 && tabBar}

        {/* ── Tab 1: Overview ── */}
        {activeTab === "overview" && (
          <div className="space-y-4">
            {/* Full-width banners — only render when there is something to show */}
            <DateChangePendingBanner requests={pendingDateRequests} />
            <DeviationSignalsBanner projectId={id} />

            {/* 2-column project health dashboard */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* LEFT column — 2/3 width on large screens */}
              <div className="space-y-4 lg:col-span-2">
                {/* T-35 — business context "up front" (demo L647). */}
                <BusinessContextCard projectId={id} />
                <RiskDashboard projectId={id} />
                <PhaseGovernanceSection projectId={id} />
                <MeetingIntelligenceCard projectId={id} />
              </div>

              {/* RIGHT column — 1/3 width sidebar */}
              <div className="space-y-4 lg:col-span-1">
                <ProjectInfoCard
                  project={project}
                  onDateChangeRequested={() =>
                    void dateChangeRequestsQuery.refetch()
                  }
                  pendingDateRequests={pendingDateRequests}
                />
                <ProjectTeamCard project={project} />
                <ProjectToolsCard project={project} />
                <LatestWeeklyReportCard projectId={id} />
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 2: Meetings ── */}
        {activeTab === "meetings" && (
          <div className="space-y-6">
            {/* Meetings — canonical per-phase meeting set + Avoma sync + CIA badges (MVP-15) */}
            <MeetingTimeline projectId={id} />
          </div>
        )}

        {/* ── Tab: Weekly Reports ── */}
        {activeTab === "weekly-reports" && (
          <div className="space-y-6">
            <WeeklyReportSection projectId={id} />
          </div>
        )}

        {/* ── Tab 3: Team & Resources ── */}
        {activeTab === "team" && (
          <div className="space-y-6">
            {/* One unified section: seven role groups, all backed by project_resources.
              Replaces the old TeamSection (free-text PM/SA/Lead on the projects row via
              UserSelect) + ResourceSection (capacity rows via ResourcePicker) pair. */}
            <UnifiedTeamSection projectId={id} />
          </div>
        )}

        {/* ── Tab 4: P0 Artifacts ── */}
        {activeTab === "phase0" && (
          // No `space-y-*` wrapper and no bottom spacer: `Phase0Page` measures its own distance to
          // the viewport bottom and subtracts exactly one page gutter, so any extra box here lands
          // BELOW the fold and reintroduces the page scroll this layout exists to remove.
          <Phase0Page projectId={id} />
        )}

        {/* ── Tab 5: Settings ── */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            {/* Automated provisioning status: GitHub / Slack / Jira / Clockify (MVP-11) */}
            <ToolsSection project={project} />

            {/* Project closure (PM / Leadership / Admin only) */}
            <ClosureSection project={project} />
          </div>
        )}
      </div>

      {/* Bottom breathing room. Suppressed on P0 Artifacts: it is an invisible 1px rule plus a 24px
          `space-y-6` gap, i.e. 25px of overflow past a workspace that was sized to end exactly at
          the page gutter — enough on its own to put a scrollbar on <main>. */}
      {!isPhase0 && <Separator className="opacity-0" />}
    </div>
  );
}

export default ProjectDetailPage;
